import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import { tokens } from '../../theme';
import fetchData from '../../helpers/fetchData';

export type ChurnKind = 'unsub' | 'engagement' | 'churn' | 'never_opened';

export interface ChurnKpiTrendModalProps {
  open: boolean;
  onClose: () => void;
  kind: ChurnKind;
  horizonDays: number;
}

interface TimeseriesPoint {
  cohort_month: string;
  cohort_size: number;
  count: number;
  median: number | null;
  avg: number | null;
}

interface TimeseriesResponse {
  kind: ChurnKind;
  horizon_days: number;
  min_cohort_age_days: number;
  earliest_cohort_start: string;
  points: TimeseriesPoint[];
}

type MetricMode = 'median' | 'rate';

interface KindMeta {
  title: string;
  subtitle: (h: number) => string;
  caveat: string;
  yLabel: string;
  mode: MetricMode;
  countLabel: string; // e.g. "Abmelder", "Nutzer ohne Opens"
}

const KIND_META: Record<ChurnKind, KindMeta> = {
  unsub: {
    title: 'Verlauf: Median Tage bis Abmeldung',
    subtitle: (h) =>
      `Median der Tage zwischen Signup und Abmelde-Klick, gemessen pro Kohorten-Monat — nur Abmeldungen innerhalb von ${h} Tagen nach Signup. Kohorten müssen mindestens ${h} Tage alt sein (sonst wäre der Vergleich unfair).`,
    caveat:
      'Warum Altersnormalisierung? Ohne die ≤-Horizont-Klammer würden jüngere Kohorten künstlich „schneller churnen“ aussehen, weil spät-abmeldende Nutzer schlicht noch nicht so weit sind. Die Horizont-Begrenzung stellt fair-vergleichbare Zahlen her.',
    yLabel: 'Median Tage',
    mode: 'median',
    countLabel: 'Abmelder',
  },
  engagement: {
    title: 'Verlauf: Median Engagement-Fenster',
    subtitle: () =>
      `Median der Tage zwischen Signup und letztem Open, pro Kohorten-Monat — nur Nutzer, die nach der 90/90-Regel als inaktiv gelten UND mindestens einmal geöffnet haben. Kohorten müssen ≥ 90 Tage alt sein.`,
    caveat:
      'Jüngste eingeschlossene Kohorte ist noch partiell zensiert: nur Nutzer, die direkt am Anfang geöffnet und dann schnell verstummt sind, sind bereits identifiziert. „Langsame“ stille Abwanderer erscheinen erst in künftigen Snapshots.',
    yLabel: 'Median Tage',
    mode: 'median',
    countLabel: 'inaktive mit Opens',
  },
  churn: {
    title: 'Verlauf: Churn innerhalb der ersten Tage (pro Kohorte)',
    subtitle: (h) =>
      `Anteil jeder Kohorte, der innerhalb von ${h} Tagen nach Signup die Liste verlassen hat (Abmeldung ODER 90/90-Inaktivität, was zuerst kam). Kohorten müssen ≥ ${h} Tage alt sein — sonst wäre der Vergleich unfair. ⚠ Unterscheidet sich bewusst von der Karten-Zahl „Churn-Quote": die Karte misst GESAMT über den ausgewählten Zeitraum (age-mixed: alte Kohorten hatten mehr Zeit zu churnen), diese Ansicht misst nur die ersten ${h} Tage pro Kohorte (age-normalisiert, fair vergleichbar).`,
    caveat:
      'Ohne die Horizon-Klammer würde die Churn-Quote jüngerer Kohorten niedrig aussehen, weil Exit-Ereignisse noch gar nicht stattfinden konnten. Bei horizon < 90 kann nur Abmeldung, aber nicht 90/90-Inaktivität, innerhalb des Fensters feuern.',
    yLabel: 'Churn-Rate',
    mode: 'rate',
    countLabel: 'Exit-Ereignisse',
  },
  never_opened: {
    title: 'Verlauf: Kein Open in den ersten Tagen (pro Kohorte)',
    subtitle: (h) =>
      `Anteil jeder Kohorte, der in den ersten ${h} Tagen nach Signup keine einzige Mail geöffnet hat. Die direkteste Lead-Qualitäts- / Welcome-Serie-Kennzahl. Kohorten müssen ≥ ${h} Tage alt sein. ⚠ Unterscheidet sich bewusst von der Karten-Zahl „Nie geöffnet": die Karte zählt nur als-inaktiv-markierte Nutzer mit null Opens jemals (sehr eng), diese Ansicht zählt ALLE Nutzer ohne Open in den ersten ${h} Tagen — inkl. derer, die später mal geöffnet haben. Breitere & fairere Kohorten-Messung.`,
    caveat:
      'Messung pro Kohorte, nicht pro Zeitpunkt. Wenn hier ein Sprung nach oben auftritt, lohnt sich ein Blick auf Lead-Quellen-Mix + Welcome-Serie-Performance im fraglichen Monat.',
    yLabel: 'Nie-geöffnet-Rate',
    mode: 'rate',
    countLabel: 'Nie-Öffner',
  },
};

const ChurnKpiTrendModal: React.FC<ChurnKpiTrendModalProps> = ({
  open,
  onClose,
  kind,
  horizonDays,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const meta = KIND_META[kind];

  const [data, setData] = useState<TimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = (await fetchData<TimeseriesResponse>(
          '/api/iterable-user-statistics/lifetime-timeseries',
          { kind, horizon_days: horizonDays },
          false,
        )) as TimeseriesResponse;
        if (!cancelled) setData(res);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Error loading timeseries:', err);
          setError('Konnte Trend-Daten nicht laden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, kind, horizonDays]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const seriesId = meta.mode === 'rate' ? 'Rate' : 'Median';
    return [
      {
        id: seriesId,
        color: colors.blueAccent[500],
        data: data.points
          .filter((p) => (meta.mode === 'rate' ? p.cohort_size > 0 : p.median !== null))
          .map((p) => {
            const y =
              meta.mode === 'rate'
                ? p.cohort_size > 0
                  ? p.count / p.cohort_size
                  : 0
                : (p.median as number);
            return {
              x: moment(p.cohort_month, 'YYYY-MM').format('MMM YY'),
              y,
              cohortMonth: p.cohort_month,
              cohortSize: p.cohort_size,
              count: p.count,
              avg: p.avg,
              median: p.median,
            };
          }),
      },
    ];
  }, [data, colors, meta.mode]);

  const LineTooltip = ({ point }: any) => (
    <Box
      sx={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {point.data.cohortMonth}
      </Typography>
      {meta.mode === 'rate' ? (
        <Typography variant="body2">
          <strong>{meta.yLabel}:</strong> {(Number(point.data.y) * 100).toFixed(1)} %
        </Typography>
      ) : (
        <>
          <Typography variant="body2">
            <strong>Median:</strong> {point.data.y} Tage
          </Typography>
          <Typography variant="body2">
            <strong>Ø:</strong> {point.data.avg !== null ? Number(point.data.avg).toFixed(1) : '–'}{' '}
            Tage
          </Typography>
        </>
      )}
      <Typography variant="body2">
        <strong>Kohorte:</strong> {point.data.cohortSize.toLocaleString('de-DE')} Nutzer
      </Typography>
      <Typography variant="body2">
        <strong>{meta.mode === 'rate' ? 'Betroffene' : 'Gezählt'}:</strong>{' '}
        {point.data.count.toLocaleString('de-DE')} {meta.countLabel}
      </Typography>
    </Box>
  );

  const subtitleHorizon = kind === 'engagement' ? 90 : (data?.horizon_days ?? horizonDays);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{meta.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color={colors.grey[300]} mb={2}>
          {meta.subtitle(subtitleHorizon)}
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Typography color={colors.redAccent[500]} sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && data && chartData[0]?.data.length > 0 && (
          <>
            <Box height={320}>
              <ResponsiveLine
                data={chartData}
                margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 'auto' }}
                curve="monotoneX"
                tooltip={LineTooltip}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -30,
                  legend: 'Signup-Monat (Kohorte)',
                  legendOffset: 48,
                  legendPosition: 'middle',
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  legend: meta.yLabel,
                  legendOffset: -45,
                  legendPosition: 'middle',
                  format:
                    meta.mode === 'rate'
                      ? (v: any) => `${(Number(v) * 100).toFixed(0)} %`
                      : undefined,
                }}
                colors={{ datum: 'color' }}
                pointSize={8}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                useMesh={true}
                enableGridX={false}
                theme={{
                  axis: {
                    domain: { line: { stroke: colors.grey[100] } },
                    legend: { text: { fill: colors.grey[100] } },
                    ticks: {
                      line: { stroke: colors.grey[100], strokeWidth: 1 },
                      text: { fill: colors.grey[100] },
                    },
                  },
                  tooltip: { container: { color: colors.primary[500] } },
                }}
              />
            </Box>

            <Typography
              variant="caption"
              color={colors.grey[300]}
              sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
            >
              {meta.caveat}
            </Typography>

            <TableContainer component={Paper} sx={{ mt: 2, backgroundColor: '#ffffff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      Kohorten-Monat
                    </TableCell>
                    {meta.mode === 'rate' ? (
                      <>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                          align="right"
                        >
                          Rate
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                          align="right"
                        >
                          Betroffene
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                          align="right"
                        >
                          Median
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                          align="right"
                        >
                          Ø
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                          align="right"
                        >
                          Gezählt
                        </TableCell>
                      </>
                    )}
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                      align="right"
                    >
                      Kohortengröße
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.points.map((p) => (
                    <TableRow key={p.cohort_month}>
                      <TableCell>{p.cohort_month}</TableCell>
                      {meta.mode === 'rate' ? (
                        <>
                          <TableCell align="right">
                            {p.cohort_size > 0
                              ? `${((p.count / p.cohort_size) * 100).toFixed(1)} %`
                              : '–'}
                          </TableCell>
                          <TableCell align="right">{p.count.toLocaleString('de-DE')}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right">
                            {p.median === null ? '–' : `${p.median} Tage`}
                          </TableCell>
                          <TableCell align="right">
                            {p.avg === null ? '–' : `${Number(p.avg).toFixed(1)} Tage`}
                          </TableCell>
                          <TableCell align="right">{p.count.toLocaleString('de-DE')}</TableCell>
                        </>
                      )}
                      <TableCell align="right">{p.cohort_size.toLocaleString('de-DE')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {!loading && !error && data && chartData[0]?.data.length === 0 && (
          <Typography color={colors.grey[100]}>
            Noch keine auswertbaren Kohorten — zu wenig Zeit seit Signup für diese Metrik.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChurnKpiTrendModal;
